/**
 * QueueTab — the visual command surface for the autonomous ticket queue.
 *
 * Two modes behind a segmented header (the panel is usually a ~400px side
 * panel — everything is designed for that width):
 *
 *  **Work** — monitoring mode, a status-grouped vertical feed ordered by
 *  attention: Running (live worker cards) → Up next (todo) → Needs
 *  attention (fix + escalate) → collapsed Review / Done. Empty groups
 *  vanish; there is no kanban — six 60px columns in a side panel was the
 *  original sin this layout replaced.
 *
 *  **Plan** — capture + shaping mode: the idea inbox (text + LA-2 mic),
 *  "Plan from selected", and the epics list.
 *
 * Chrome: tabs + a single progress strip + run controls (▶ Run = loop,
 * ⋯ = the individual plan/work/verify/test verbs and queue settings).
 * A slim status strip appears at the bottom only while a script runs
 * (or has output to show) — calm otherwise.
 *
 * Live updates: the queueStore subscribes to the existing
 * `file-tree-event` channel and re-fetches whatever folder changed.
 */
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Inbox as InboxIcon,
  ListTodo,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Wrench,
  Eye,
  Mic,
  Square,
  Plus,
  X,
  Play,
  ShieldCheck,
  ClipboardList,
  Trash2,
  Settings as SettingsIcon,
  RefreshCw,
  Layers,
  Cpu,
  Bot,
  Gauge,
  Triangle,
  Activity,
  ChevronRight,
  Ban,
  Terminal,
  Zap,
  MoreHorizontal,
  MessageSquarePlus,
} from 'lucide-react';
import { useBackend } from '@/services/backend';
import { useProjectStore } from '@/stores/projectStore';
import { useTerminalStore } from '@/stores/terminalStore';
import { useUIStore } from '@/stores/uiStore';
import { showToast } from '@/components/ui/Toast';
import { uint8ToBase64 } from '@/lib/base64';

/** Prompt typed into the interactive agent terminal by "Run in agent" — tells the
 *  human-initiated session to work the queue per the project's own WORKFLOW.md.
 *  The agent (Claude/Gemini/…) handles its own engine matching. */
/** The queue-runner prompt typed into the interactive agent terminal. ONE
 *  interactive session that parallelizes via its OWN subagents (e.g. Claude's
 *  Task tool) — not multiple terminals — so it stays squarely interactive use.
 *  `n` = how many subagents to keep in flight (from work.maxConcurrent). */
const armQueuePrompt = (n: number): string =>
  'You are the AtelierCode queue runner for this project. Read .ateliercode/work/WORKFLOW.md and ' +
  'context/LESSONS.md first. Then drain the queue, parallelizing with SUBAGENTS when you can:\n' +
  `1. Find the READY tickets assigned to you (engine matches you AND every dependency is in done/), checking fix/ then todo/.\n` +
  `2. If you have a subagent/Task tool, keep up to ${n} subagents in flight — one per ready ticket. Each subagent: ` +
  'atomically claim its ticket (move to in-progress/), implement per spec, verify with the project’s build/typecheck/tests, ' +
  'then move it review→done (or to fix/ on failure). If you have no subagents, do them one at a time.\n' +
  `3. As tickets finish and unblock others, dispatch more — keep up to ${n} working.\n` +
  '4. If nothing is ready for you but some assigned tickets are BLOCKED, hold ~15 minutes then re-check from step 1.\n' +
  '5. Stop when no tickets remain for you, and say so.\n' +
  'Never let two workers claim the same ticket. Start now.';
import {
  useQueueStore,
  useQueueWatcher,
  QUEUE_FOLDERS,
  type QueueFolder,
  type TicketSummary,
  type IdeaSummary,
  type EpicSummary,
  type QueueEvent,
} from '@/stores/queueStore';
import {
  DEFAULT_WORK_QUEUE,
  type QueueRole,
  type WorkQueueConfig,
  type AteliercodeConfig,
} from '@/lib/projectSettings';
import { live as veLive, type LiveSession as VeLiveSession } from '@/lib/voice-engine';
import { logger } from '@/lib/logger';

const TAG = 'QueueTab';

// A second-resolution clock that re-renders subscribers each tick. Used by the
// elapsed timers and the relative event-feed times so they stay live without
// each component owning its own interval.
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

// Compact elapsed string: 3s / 1m04s / 1h02m. Used for "this agent has been
// working for…" on in-progress tickets.
function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m${String(rs).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h${String(rm).padStart(2, '0')}m`;
}

// Strip basic ANSI escape sequences (CSI color/cursor codes, plus OSC title
// sequences) from a worker log so the live-log drawer renders clean monospace
// text. The log is mostly plain, but agents occasionally emit colored output.
function stripAnsi(s: string): string {
  return s
    // CSI sequences: ESC [ … final-letter  (colors, cursor moves, erases)
    .replace(/\x1b\[[0-9;?]*[A-Za-z]/g, '')
    // OSC sequences: ESC ] … BEL | ESC \   (window/title sets)
    .replace(/\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g, '')
    // Stray lone escapes
    .replace(/\x1b[=>]/g, '');
}

// Load the project's WorkQueueConfig from .ateliercode/config.json. Shared by
// the Running section (needs maxConcurrent) and the header run controls
// (concurrency + roles in the ⋯ menu). Returns DEFAULT_WORK_QUEUE until loaded.
function useWorkConfig(projectId: string): WorkQueueConfig {
  const backend = useBackend();
  const [work, setWork] = useState<WorkQueueConfig>(DEFAULT_WORK_QUEUE);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await backend.files.readAteliercodeConfig(projectId);
        if (cancelled) return;
        const parsed: AteliercodeConfig = raw ? JSON.parse(raw) : {};
        setWork({
          path: parsed.work?.path ?? DEFAULT_WORK_QUEUE.path,
          maxAttempts: parsed.work?.maxAttempts ?? DEFAULT_WORK_QUEUE.maxAttempts,
          maxConcurrent: parsed.work?.maxConcurrent ?? DEFAULT_WORK_QUEUE.maxConcurrent,
          roles: { ...DEFAULT_WORK_QUEUE.roles, ...(parsed.work?.roles ?? {}) } as Record<QueueRole, string>,
          agents: { ...DEFAULT_WORK_QUEUE.agents, ...(parsed.work?.agents ?? {}) },
        });
      } catch (e) {
        logger.debug(TAG, 'config load failed', e);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, backend]);
  return work;
}

// Resolve a ticket's display engine. Local (Qwen) is the default when unset.
function engineOf(t: TicketSummary): 'local' | 'claude' {
  return t.engine ?? 'local';
}

const FOLDER_LABELS: Record<QueueFolder, { label: string; icon: typeof ListTodo; tone: string }> = {
  todo:          { label: 'Todo',        icon: ListTodo,      tone: 'text-base-content/70' },
  'in-progress': { label: 'In progress', icon: Loader2,       tone: 'text-blue-500' },
  review:        { label: 'Review',      icon: Eye,           tone: 'text-amber-500' },
  fix:           { label: 'Fix',         icon: Wrench,        tone: 'text-orange-500' },
  done:          { label: 'Done',        icon: CheckCircle2,  tone: 'text-green-500' },
  escalate:      { label: 'Escalate',    icon: AlertTriangle, tone: 'text-red-500' },
};

interface QueueTabProps {
  projectId?: string;
}

export default function QueueTab({ projectId }: QueueTabProps) {
  const backend = useBackend();
  const project = useProjectStore((s) => s.currentProject);
  const effectiveProjectId = projectId ?? project?.id;
  useQueueWatcher(effectiveProjectId);

  const byFolder = useQueueStore((s) => s.byFolder);
  const inbox = useQueueStore((s) => s.inbox);
  const epics = useQueueStore((s) => s.epics);
  const claudeWaiting = useQueueStore((s) => s.claudeWaiting);
  const blocking = useQueueStore((s) => s.blocking);
  const workerIsClaude = useQueueStore((s) => s.workerIsClaude);
  const bannerDismissedCount = useQueueStore((s) => s.claudeBannerDismissedCount);
  const dismissClaudeBanner = useQueueStore((s) => s.dismissClaudeBanner);
  const inProgressSince = useQueueStore((s) => s.inProgressSince);
  const events = useQueueStore((s) => s.events);
  const loading = useQueueStore((s) => s.loading);
  const error = useQueueStore((s) => s.error);
  const refresh = useQueueStore((s) => s.refresh);

  const work = useWorkConfig(effectiveProjectId ?? '');
  const run = useQueueRun(effectiveProjectId ?? '');

  // True when this project already has a live agent terminal — used to DISABLE
  // "Run in agent" so a second runner can't be spawned for the same agent (one
  // terminal per agent; parallelism happens via that agent's subagents, not more
  // terminals). Reactive selector so the button enables/disables live.
  const agentTerminalId = useTerminalStore((s) => {
    const path = project?.root_path;
    if (!path) return null;
    for (const sess of s.sessions.values()) {
      if (sess.projectPath === path && sess.isAgent && sess.status === 'active' && sess.id > 0) return sess.id;
    }
    return null;
  });

  // "Run in agent" — ToS-safe interactive kickoff. The click IS the human action
  // that drives ONE interactive agent session (not a headless job, not a fleet):
  // type the subagent-parallel drain-loop prompt + Enter into the project's live
  // agent terminal. The session parallelizes via its own subagents.
  const runInteractive = useCallback(() => {
    if (agentTerminalId == null) {
      showToast('Open the Terminal tab first (your agent launches there), then click Run in agent.', 'error');
      return;
    }
    const n = Math.max(1, work.maxConcurrent ?? 3);
    const bytes = new TextEncoder().encode(armQueuePrompt(n) + '\r');
    backend.terminal
      .write(agentTerminalId, uint8ToBase64(bytes))
      .then(() => showToast(`Sent the queue runner prompt — your agent will drain its tickets (up to ${n} in parallel via subagents).`, 'success'))
      .catch(() => showToast('Could not send to the agent terminal.', 'error'));
  }, [backend, agentTerminalId, work.maxConcurrent]);

  /** Which mode the panel shows: monitoring (work) or capture/shaping (plan). */
  const [tab, setTab] = useState<'work' | 'plan'>('work');
  // Layout follows the panel's real width, not the window: docked at ~400px
  // the Work tab is a vertical focus feed; pulled wide / maximized it becomes
  // a swim-lane board (the pipeline shape is information — flow, bottlenecks,
  // and stuck tickets read spatially, like Jira/ADO boards).
  const rootRef = useRef<HTMLDivElement>(null);
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => setWide(el.offsetWidth >= BOARD_MIN_WIDTH));
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const [activeTicket, setActiveTicket] = useState<TicketSummary | null>(null);
  const [activeBody, setActiveBody] = useState<string>('');
  // The in-progress ticket whose live worker output is being watched (a separate
  // drawer from the markdown TicketDrawer above). null = closed.
  const [watchTicket, setWatchTicket] = useState<TicketSummary | null>(null);

  const openTicket = useCallback(async (t: TicketSummary) => {
    if (!effectiveProjectId) return;
    setActiveTicket(t);
    setActiveBody('Loading…');
    try {
      const body = await backend.queue.readTicket(effectiveProjectId, t.folder, t.filename);
      setActiveBody(body);
    } catch (e) {
      setActiveBody(`Error: ${e instanceof Error ? e.message : String(e)}`);
    }
  }, [effectiveProjectId, backend]);

  // Open the live-log drawer for an active worker.
  const watchWorker = useCallback((t: TicketSummary) => {
    setWatchTicket(t);
  }, []);

  if (!effectiveProjectId) {
    return <div className="flex h-full items-center justify-center text-base-content/50">Select a project to view its queue.</div>;
  }

  return (
    <div ref={rootRef} className="flex flex-col h-full bg-base-100 text-base-content">
      <PanelChrome
        tab={tab}
        onTab={setTab}
        byFolder={byFolder}
        inboxCount={inbox.filter((i) => !i.converted).length}
        events={events}
        onRefresh={refresh}
        loading={loading}
        error={error}
        run={run}
        work={work}
        onRunInteractive={runInteractive}
        canRunInteractive={agentTerminalId != null}
      />
      {!workerIsClaude && claudeWaiting.length > bannerDismissedCount && (
        <ClaudeWaitingBanner tickets={claudeWaiting} blocking={blocking} onDismiss={dismissClaudeBanner} />
      )}
      {tab === 'work' ? (
        wide ? (
          <BoardView
            byFolder={byFolder}
            maxConcurrent={work.maxConcurrent ?? 3}
            inProgressSince={inProgressSince}
            onOpenTicket={openTicket}
            onWatch={watchWorker}
          />
        ) : (
          <WorkView
            byFolder={byFolder}
            maxConcurrent={work.maxConcurrent ?? 3}
            inProgressSince={inProgressSince}
            onOpenTicket={openTicket}
            onWatch={watchWorker}
            onRun={() => run.start('loop')}
            running={run.running !== null}
          />
        )
      ) : (
        <PlanView projectId={effectiveProjectId} inbox={inbox} epics={epics} />
      )}
      <RunStatusStrip run={run} />
      {activeTicket && (
        <TicketDrawer
          ticket={activeTicket}
          body={activeBody}
          onClose={() => setActiveTicket(null)}
        />
      )}
      {watchTicket && (
        <WorkerLogDrawer
          projectId={effectiveProjectId}
          ticket={watchTicket}
          onClose={() => setWatchTicket(null)}
        />
      )}
    </div>
  );
}

// ─── Panel chrome: tabs + progress strip + run controls ─────────────

const RUN_VERBS: { verb: string; label: string; icon: typeof Play; hint: string }[] = [
  { verb: 'plan',   label: 'Plan',   icon: ListTodo,      hint: 'break ideas into tickets' },
  { verb: 'work',   label: 'Work',   icon: Play,          hint: 'execute one batch of tickets' },
  { verb: 'verify', label: 'Verify', icon: ShieldCheck,   hint: 'judge tickets in review/' },
  { verb: 'test',   label: 'Test',   icon: ClipboardList, hint: 'run the project tests' },
  { verb: 'loop',   label: 'Loop',   icon: RefreshCw,     hint: 'work + verify until drained' },
];

function PanelChrome({
  tab,
  onTab,
  byFolder,
  inboxCount,
  events,
  onRefresh,
  loading,
  error,
  run,
  work,
  onRunInteractive,
  canRunInteractive,
}: {
  tab: 'work' | 'plan';
  onTab: (t: 'work' | 'plan') => void;
  byFolder: Record<QueueFolder, TicketSummary[]>;
  inboxCount: number;
  events: QueueEvent[];
  onRefresh: () => Promise<void>;
  loading: boolean;
  error: string | null;
  run: QueueRunState;
  work: WorkQueueConfig;
  onRunInteractive: () => void;
  canRunInteractive: boolean;
}) {
  const openSettingsTo = useUIStore((s) => s.openSettingsTo);
  const done = (byFolder.done ?? []).length;
  const total = QUEUE_FOLDERS.reduce((n, f) => n + (byFolder[f] ?? []).length, 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const tabBtn = (id: 'work' | 'plan', Icon: typeof Zap, label: string, badge?: number) => (
    <button
      type="button"
      onClick={() => onTab(id)}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
        tab === id
          ? 'bg-base-100 text-base-content shadow-sm'
          : 'text-base-content/50 hover:text-base-content/80'
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {badge !== undefined && badge > 0 && (
        <span className="px-1 rounded-full bg-primary/20 text-primary text-[9px] font-mono leading-4">{badge}</span>
      )}
    </button>
  );

  return (
    <div className="border-b border-base-300 bg-base-200">
      <div className="flex items-center gap-2 px-2.5 pt-2 pb-1.5">
        <div className="flex gap-0.5 p-0.5 rounded-lg bg-base-300/70">
          {tabBtn('work', Zap, 'Work')}
          {tabBtn('plan', ListTodo, 'Plan', inboxCount)}
        </div>
        <div className="flex-1" />
        {error && <span className="w-2 h-2 rounded-full bg-error shrink-0" title={error} />}
        <button
          type="button"
          onClick={() => void onRefresh()}
          className="btn btn-ghost btn-xs btn-square"
          title="Refresh"
          disabled={loading}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        {run.running ? (
          <button
            type="button"
            className="btn btn-error btn-xs gap-1"
            onClick={() => void run.stop()}
            title={`Stop the running ${run.running} script (and its workers)`}
          >
            <Square className="w-3 h-3" />
            Stop
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-xs gap-1"
            onClick={() => void run.start('loop')}
            title="Run the loop: work + verify until the queue drains"
          >
            <Play className="w-3 h-3" />
            Run
          </button>
        )}
        <button
          type="button"
          className="btn btn-ghost btn-xs gap-1"
          onClick={onRunInteractive}
          disabled={!canRunInteractive}
          title={
            canRunInteractive
              ? 'Run the queue in your interactive agent terminal — one session, parallelized via its own subagents (ToS-safe; not a headless job)'
              : 'Open the Terminal tab first — your agent launches there, then this arms it to drain the queue'
          }
        >
          <Bot className="w-3 h-3" />
          Run in agent
        </button>
        <div className="dropdown dropdown-end">
          <button type="button" tabIndex={0} className="btn btn-ghost btn-xs btn-square" title="Queue actions">
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
          <ul tabIndex={0} className="dropdown-content z-50 menu menu-sm mt-1 w-56 p-1.5 rounded-lg bg-base-200 border border-base-300 shadow-xl">
            {RUN_VERBS.map((v) => (
              <li key={v.verb}>
                <button
                  type="button"
                  disabled={run.running !== null}
                  onClick={() => void run.start(v.verb)}
                  title={v.hint}
                >
                  <v.icon className="w-3.5 h-3.5" />
                  {v.label}
                  <span className="ml-auto text-[9px] text-base-content/40">{v.hint}</span>
                </button>
              </li>
            ))}
            <li className="pt-0.5 mt-0.5 border-t border-base-300">
              <button type="button" onClick={() => openSettingsTo('project-queue')}>
                <Gauge className="w-3.5 h-3.5" />
                Concurrency
                <span className="ml-auto font-mono text-[10px]">{work.maxConcurrent ?? 3}</span>
              </button>
            </li>
            <li>
              <button type="button" onClick={() => openSettingsTo('project-queue')} title={
                (['planner', 'worker', 'validator', 'tester'] as QueueRole[])
                  .map((r) => `${r}: ${work.roles[r]}`).join('\n')
              }>
                <SettingsIcon className="w-3.5 h-3.5" />
                Roles &amp; agents…
              </button>
            </li>
          </ul>
        </div>
      </div>
      {total > 0 && (
        <div className="flex items-center gap-2 px-2.5 pb-1.5" title={`${done} of ${total} tickets done (${pct}%)`}>
          <div className="flex-1 h-1 rounded-full bg-base-300 overflow-hidden">
            <div
              className="h-full bg-green-500 transition-[width] duration-500 ease-out"
              style={{ width: `${pct}%` }}
            />
          </div>
          <span className="text-[9px] font-mono text-base-content/40 tabular-nums shrink-0">{done}/{total}</span>
        </div>
      )}
      <EventFeed events={events} />
    </div>
  );
}

// A single-line scrolling feed of the most recent board moves ("061 claimed ·
// 003 → review …"), derived from the store's folder diffs. Hidden until the
// first move is observed. Newest first; fades as it ages.
function EventFeed({ events }: { events: QueueEvent[] }) {
  const now = useNow(1000);
  if (events.length === 0) return null;
  return (
    <div className="flex items-center gap-1 px-3 py-1 border-t border-base-300/60 bg-base-100/40 overflow-hidden">
      <Activity className="w-3 h-3 text-base-content/30 shrink-0" />
      <div className="flex items-center gap-3 overflow-x-auto whitespace-nowrap text-[10px] no-scrollbar">
        {events.map((e) => {
          const age = now - e.atMs;
          const fresh = age < 4000;
          return (
            <span
              key={e.id}
              className={`inline-flex items-center gap-1 transition-opacity duration-700 ${
                fresh ? 'opacity-100' : 'opacity-60'
              }`}
            >
              <span className="font-mono text-base-content/60">{e.ticketId}</span>
              <span className={
                e.label === 'claimed' ? 'text-blue-500'
                : e.label === '→ done' ? 'text-green-500'
                : 'text-base-content/50'
              }>{e.label}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── Claude-waiting banner ─────────────────────────────────────────

// Surfaces ready `engine:claude` tickets in todo/+fix/ that the auto-loop
// won't run (cost rule: claude tickets run in the user's interactive session,
// not the unattended loop). Derived in the store from the folders it already
// loads; the marker file <work>/.claude-ready.json is the dispatcher's
// authoritative signal, but counting claude-engine tickets here is equivalent
// and needs no extra IPC. Calm: hidden entirely when nothing is waiting.
// `blocking` (from the store) escalates this from a calm advisory into a hard
// stop banner: when the loop has drained ALL local work (none in-progress, none
// ready) and the only ready tickets are `engine:claude`, the queue cannot make
// progress without the user — so we surface it loud, at the top, with the
// gating filenames. Otherwise it's the gentle "some tickets need Claude" note.
//
// Guardrails (a 205-claude-ticket board taught us these): the parent hides the
// banner entirely when the project's configured worker IS Claude (then claude
// tickets are the loop's normal diet, nothing is "blocked") and after the user
// dismisses it (the X — stays hidden until MORE claude tickets arrive). The
// chip list is capped so the banner stays a banner, never a wall.

/** Max ticket chips shown before collapsing into a "+N more" tail. */
const BANNER_MAX_CHIPS = 8;

function ClaudeWaitingBanner({ tickets, blocking, onDismiss }: {
  tickets: TicketSummary[];
  blocking: boolean;
  onDismiss: () => void;
}) {
  if (tickets.length === 0) return null;
  const n = tickets.length;
  const shown = tickets.slice(0, BANNER_MAX_CHIPS);
  const overflow = n - shown.length;

  const chips = (chipClass: string, iconClass: string) => (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {shown.map((t) => (
        <span
          key={`${t.folder}/${t.filename}`}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] ${chipClass}`}
          title={`${t.folder}/${t.filename}`}
        >
          <Bot className={`w-3 h-3 ${iconClass}`} />
          {t.filename}
        </span>
      ))}
      {overflow > 0 && (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded font-mono text-[10px] ${chipClass}`}>
          +{overflow} more
        </span>
      )}
    </div>
  );

  const dismissBtn = (
    <button
      onClick={onDismiss}
      className="btn btn-ghost btn-xs btn-circle shrink-0 ml-auto"
      title="Dismiss (reappears when more Claude tickets arrive)"
      aria-label="Dismiss"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  );

  if (blocking) {
    return (
      <div className="px-3 py-2.5 border-b-2 border-error bg-error/15 text-base-content">
        <div className="flex items-start gap-2 text-xs">
          <Ban className="w-4 h-4 mt-0.5 shrink-0 text-error" />
          <div className="min-w-0">
            <div className="font-bold text-error text-sm">
              Blocked — the queue has nothing else to run
            </div>
            <div className="mt-0.5 text-base-content/80">
              All local work is done. These {n} ticket{n === 1 ? '' : 's'} need Claude
              before the queue can continue — run them in your session:
            </div>
            {chips('bg-error/20 border border-error/40 text-base-content/90', 'text-error')}
          </div>
          {dismissBtn}
        </div>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-b border-base-300 bg-warning/10 text-warning-content">
      <div className="flex items-start gap-2 text-xs">
        <Triangle className="w-3.5 h-3.5 mt-0.5 shrink-0 fill-warning text-warning" />
        <div className="min-w-0">
          <div className="font-medium text-base-content">
            {n} ticket{n === 1 ? '' : 's'} need Claude — run them in your session
          </div>
          {chips('bg-base-200 border border-base-300 text-base-content/70', 'text-primary')}
          <div className="mt-1 text-[10px] text-base-content/50">
            The auto-loop skips Claude tickets by the cost rule. Execute them
            yourself, then they&apos;ll flow to <code>review/</code>.
          </div>
        </div>
        {dismissBtn}
      </div>
    </div>
  );
}

// ─── Worker lanes ──────────────────────────────────────────────────

// A compact strip of the project's `maxConcurrent` worker slots, shown above
// the board. In-progress tickets are mapped to lanes by a STABLE order (sorted
// by when the store first saw them in-progress) so a given ticket keeps its
// lane; when one finishes and another is claimed, the new card visibly
// fades/slides in via the keyed CSS transition. Empty lanes read "idle".
// ─── Work view: the status-grouped focus feed ───────────────────────

/** Rows shown per list before collapsing behind a "show all" toggle. */
const LIST_CAP = 15;

function SectionLabel({ icon: Icon, label, count, tone = 'text-base-content/40' }: {
  icon: typeof Cpu;
  label: string;
  count?: string | number;
  tone?: string;
}) {
  return (
    <div className={`flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${tone}`}>
      <Icon className="w-3 h-3" />
      {label}
      {count !== undefined && (
        <span className="font-mono normal-case tracking-normal opacity-80">{count}</span>
      )}
    </div>
  );
}

function WorkView({
  byFolder,
  maxConcurrent,
  inProgressSince,
  onOpenTicket,
  onWatch,
  onRun,
  running,
}: {
  byFolder: Record<QueueFolder, TicketSummary[]>;
  maxConcurrent: number;
  inProgressSince: Record<string, number>;
  onOpenTicket: (t: TicketSummary) => void;
  onWatch: (t: TicketSummary) => void;
  onRun: () => void;
  running: boolean;
}) {
  const active = byFolder['in-progress'] ?? [];
  const todo = byFolder.todo ?? [];
  // fix + escalate are both "the loop couldn't finish this" — one group, the
  // folder chip on each row tells them apart.
  const attention = [...(byFolder.fix ?? []), ...(byFolder.escalate ?? [])];
  const review = byFolder.review ?? [];
  const done = byFolder.done ?? [];
  const boardEmpty =
    active.length + todo.length + attention.length + review.length + done.length === 0;

  if (boardEmpty) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2 text-base-content/40 p-6 text-center">
        <ClipboardList className="w-8 h-8 opacity-30" />
        <div className="text-sm font-medium text-base-content/60">The queue is empty</div>
        <div className="text-xs max-w-52">
          Capture ideas in the <span className="font-medium">Plan</span> tab, then break them into
          tickets — they'll show up here.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto pb-2">
      <RunningSection
        active={active}
        maxConcurrent={maxConcurrent}
        inProgressSince={inProgressSince}
        onOpen={onOpenTicket}
        onWatch={onWatch}
        onRun={onRun}
        running={running}
        hasReadyWork={todo.length + attention.length > 0}
      />
      {todo.length > 0 && (
        <div className="px-3 pt-3">
          <SectionLabel icon={ListTodo} label="Up next" count={todo.length} />
          <TicketList tickets={todo} onOpen={onOpenTicket} />
        </div>
      )}
      {attention.length > 0 && (
        <div className="px-3 pt-3">
          <SectionLabel icon={AlertTriangle} label="Needs attention" count={attention.length} tone="text-warning/80" />
          <TicketList tickets={attention} onOpen={onOpenTicket} showFolder />
        </div>
      )}
      {review.length > 0 && (
        <CollapsibleSection icon={Eye} label="Review" tickets={review} onOpen={onOpenTicket} />
      )}
      {done.length > 0 && (
        <CollapsibleSection icon={CheckCircle2} label="Done" tickets={done} onOpen={onOpenTicket} />
      )}
    </div>
  );
}

// The hero of the Work tab: full-width live worker cards (this panel is
// narrow — the old N-column lane grid produced 60px unreadable cards).
// Idle capacity is one quiet line of text, not N dashed placeholder boxes.
function RunningSection({
  active,
  maxConcurrent,
  inProgressSince,
  onOpen,
  onWatch,
  onRun,
  running,
  hasReadyWork,
}: {
  active: TicketSummary[];
  maxConcurrent: number;
  inProgressSince: Record<string, number>;
  onOpen: (t: TicketSummary) => void;
  onWatch: (t: TicketSummary) => void;
  onRun: () => void;
  running: boolean;
  hasReadyWork: boolean;
}) {
  const now = useNow(1000);
  const ordered = [...active].sort((a, b) => {
    const ta = inProgressSince[a.filename] ?? 0;
    const tb = inProgressSince[b.filename] ?? 0;
    return ta - tb || a.filename.localeCompare(b.filename);
  });
  const idle = Math.max(0, maxConcurrent - ordered.length);

  return (
    <div className="px-3 pt-3">
      <SectionLabel
        icon={Cpu}
        label="Running"
        count={`${ordered.length}/${maxConcurrent}`}
        tone={ordered.length > 0 ? 'text-blue-500/80' : 'text-base-content/40'}
      />
      {ordered.length === 0 ? (
        <div className="mt-1.5 rounded-md border border-dashed border-base-300 px-3 py-2.5 text-[11px] text-base-content/40 flex items-center gap-2">
          {hasReadyWork && !running ? (
            <>
              Nothing running.
              <button type="button" className="link link-primary no-underline hover:underline" onClick={onRun}>
                Start the loop
              </button>
              to pick up the queue.
            </>
          ) : (
            <>No workers running.</>
          )}
        </div>
      ) : (
        <div className="mt-1.5 space-y-1.5">
          {/* Keyed by ticket so a handoff remounts the card and re-fires the
              slide-in animation. */}
          {ordered.map((t) => (
            <RunningCard
              key={t.filename}
              ticket={t}
              now={now}
              since={inProgressSince[t.filename]}
              onOpen={onOpen}
              onWatch={onWatch}
            />
          ))}
          {idle > 0 && (
            <div className="text-[10px] text-base-content/30 px-1">
              {idle} idle slot{idle === 1 ? '' : 's'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RunningCard({
  ticket,
  now,
  since,
  onOpen,
  onWatch,
}: {
  ticket: TicketSummary;
  now: number;
  since: number | undefined;
  onOpen: (t: TicketSummary) => void;
  onWatch: (t: TicketSummary) => void;
}) {
  const engine = engineOf(ticket);
  const isClaude = engine === 'claude';
  const elapsed = since ? fmtElapsed(now - since) : '—';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(ticket)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(ticket); } }}
      title={`${ticket.filename} · ${engine} · ${elapsed}`}
      className="group relative rounded-md border border-blue-500/40 bg-blue-500/10 px-2.5 py-2 text-left cursor-pointer
        animate-[queue-lane-in_320ms_ease-out] hover:bg-blue-500/20 transition-colors"
    >
      <div className="flex items-center gap-2">
        {/* Breathing live dot — calmer than a spinner, unmistakably alive. */}
        <span className="relative flex w-2 h-2 shrink-0">
          <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-60 animate-ping [animation-duration:2s]" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-blue-500" />
        </span>
        <span className="flex-1 truncate text-xs font-medium">{ticket.title}</span>
        <span className="font-mono text-[10px] text-blue-500/90 tabular-nums shrink-0">{elapsed}</span>
      </div>
      <div className="flex items-center gap-2 mt-1 pl-4 text-[10px]">
        <span className="font-mono text-base-content/40">{ticket.filename.split('-')[0]}</span>
        <span className={`inline-flex items-center gap-1 ${isClaude ? 'text-primary' : 'text-base-content/50'}`}>
          {isClaude ? <Bot className="w-2.5 h-2.5" /> : <Cpu className="w-2.5 h-2.5" />}
          {engine}
        </span>
        {ticket.attempt_count > 0 && (
          <span className="text-base-content/40">attempt {ticket.attempt_count}</span>
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onWatch(ticket); }}
          title="Watch this worker's live output"
          className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium
            bg-blue-500/15 text-blue-500 hover:bg-blue-500/30 transition-colors"
        >
          <Terminal className="w-3 h-3" />
          log
        </button>
      </div>
    </div>
  );
}

// One ticket as a compact row: mono id · title · (folder chip) · engine chip.
function TicketRow({
  ticket,
  onOpen,
  showFolder = false,
}: {
  ticket: TicketSummary;
  onOpen: (t: TicketSummary) => void;
  showFolder?: boolean;
}) {
  const folderMeta = FOLDER_LABELS[ticket.folder];
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(ticket)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(ticket); } }}
      title={ticket.filename}
      className="group flex items-center gap-2 px-1.5 py-[5px] rounded-md hover:bg-base-200 cursor-pointer"
    >
      <span className="font-mono text-[10px] text-base-content/35 tabular-nums w-7 shrink-0 text-right">
        {ticket.filename.split('-')[0]}
      </span>
      <span className="flex-1 truncate text-xs">{ticket.title}</span>
      {ticket.attempt_count > 0 && (
        <span className="font-mono text-[9px] text-warning shrink-0" title={`${ticket.attempt_count} failed attempt${ticket.attempt_count === 1 ? '' : 's'}`}>
          ×{ticket.attempt_count}
        </span>
      )}
      {showFolder && folderMeta && (
        <span className={`text-[9px] font-medium shrink-0 ${folderMeta.tone}`}>{folderMeta.label.toLowerCase()}</span>
      )}
      <EngineChip ticket={ticket} />
    </div>
  );
}

// A ticket list capped at LIST_CAP rows with a "show all" expander.
function TicketList({
  tickets,
  onOpen,
  showFolder = false,
}: {
  tickets: TicketSummary[];
  onOpen: (t: TicketSummary) => void;
  showFolder?: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? tickets : tickets.slice(0, LIST_CAP);
  const hidden = tickets.length - visible.length;
  return (
    <div className="mt-1">
      {visible.map((t) => (
        <TicketRow key={`${t.folder}/${t.filename}`} ticket={t} onOpen={onOpen} showFolder={showFolder} />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          className="px-1.5 py-1 text-[10px] text-base-content/40 hover:text-base-content/70"
          onClick={() => setShowAll(true)}
        >
          show all {tickets.length} ▸
        </button>
      )}
      {showAll && tickets.length > LIST_CAP && (
        <button
          type="button"
          className="px-1.5 py-1 text-[10px] text-base-content/40 hover:text-base-content/70"
          onClick={() => setShowAll(false)}
        >
          collapse ▴
        </button>
      )}
    </div>
  );
}

// Review / Done live behind a collapsed one-line header — they're archives,
// not work. Click to expand.
function CollapsibleSection({
  icon,
  label,
  tickets,
  onOpen,
}: {
  icon: typeof Eye;
  label: string;
  tickets: TicketSummary[];
  onOpen: (t: TicketSummary) => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = icon;
  return (
    <div className="px-3 pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 w-full text-[10px] font-semibold uppercase tracking-[0.12em] text-base-content/40 hover:text-base-content/70"
      >
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} />
        <Icon className="w-3 h-3" />
        {label}
        <span className="font-mono normal-case tracking-normal opacity-80">{tickets.length}</span>
      </button>
      {open && <TicketList tickets={tickets} onOpen={onOpen} />}
    </div>
  );
}

// ─── Board view: swim lanes for wide layouts ─────────────────────────

/** Panel width (px) at which the Work tab switches feed → swim-lane board. */
const BOARD_MIN_WIDTH = 860;

/** Done is an archive, not work — its column starts collapsed to a count. */
const BOARD_COLLAPSED_DEFAULT: QueueFolder[] = ['done'];

function BoardView({
  byFolder,
  maxConcurrent,
  inProgressSince,
  onOpenTicket,
  onWatch,
}: {
  byFolder: Record<QueueFolder, TicketSummary[]>;
  maxConcurrent: number;
  inProgressSince: Record<string, number>;
  onOpenTicket: (t: TicketSummary) => void;
  onWatch: (t: TicketSummary) => void;
}) {
  const now = useNow(1000);
  const [expanded, setExpanded] = useState<Partial<Record<QueueFolder, boolean>>>({});

  return (
    <div className="flex-1 flex gap-2 p-2 overflow-x-auto overflow-y-hidden">
      {QUEUE_FOLDERS.map((folder) => {
        const meta = FOLDER_LABELS[folder];
        const Icon = meta.icon;
        const tickets = byFolder[folder] ?? [];
        const isLive = folder === 'in-progress';
        const collapsed = BOARD_COLLAPSED_DEFAULT.includes(folder) && !expanded[folder] && tickets.length > 0;

        return (
          <div
            key={folder}
            className={`flex flex-col min-w-44 flex-1 rounded-lg border ${
              isLive
                ? 'bg-blue-500/5 border-blue-500/30'
                : 'bg-base-200/60 border-base-300'
            } ${collapsed ? 'max-w-36' : ''}`}
          >
            <button
              type="button"
              disabled={!BOARD_COLLAPSED_DEFAULT.includes(folder)}
              onClick={() => setExpanded((e) => ({ ...e, [folder]: !e[folder] }))}
              className={`flex items-center gap-1.5 px-2.5 py-2 text-[11px] font-semibold uppercase tracking-wide ${meta.tone} ${
                BOARD_COLLAPSED_DEFAULT.includes(folder) ? 'cursor-pointer hover:bg-base-300/30' : 'cursor-default'
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${isLive && tickets.length > 0 ? 'animate-pulse' : ''}`} />
              {meta.label}
              <span className="font-mono normal-case text-base-content/40">{tickets.length}</span>
              {isLive && (
                <span className="ml-auto font-mono normal-case text-[10px] text-blue-500/70">
                  {tickets.length}/{maxConcurrent}
                </span>
              )}
              {BOARD_COLLAPSED_DEFAULT.includes(folder) && tickets.length > 0 && (
                <ChevronRight className={`ml-auto w-3 h-3 transition-transform ${expanded[folder] ? 'rotate-90' : ''}`} />
              )}
            </button>

            {collapsed ? (
              <button
                type="button"
                onClick={() => setExpanded((e) => ({ ...e, [folder]: true }))}
                className="px-2.5 py-2 text-xs text-base-content/40 hover:text-base-content/70 text-left"
              >
                {tickets.length} archived ▸
              </button>
            ) : (
              <div className="flex-1 px-1.5 pb-1.5 space-y-1.5 overflow-y-auto">
                {tickets.length === 0 ? (
                  <div className="text-[11px] text-base-content/25 text-center py-5 select-none">—</div>
                ) : (
                  tickets.map((t) => (
                    <BoardCard
                      key={t.filename}
                      ticket={t}
                      live={isLive}
                      since={isLive ? inProgressSince[t.filename] : undefined}
                      now={now}
                      onOpen={onOpenTicket}
                      onWatch={onWatch}
                    />
                  ))
                )}
                {isLive && tickets.length < maxConcurrent && (
                  <div className="rounded-md border border-dashed border-base-300/70 px-2 py-1.5 text-[10px] text-base-content/30 text-center select-none">
                    {maxConcurrent - tickets.length} idle slot{maxConcurrent - tickets.length === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// A swim-lane card. The in-progress column doubles as the worker lane:
// `live` cards get the breathing dot, elapsed timer, and log button.
function BoardCard({
  ticket,
  live,
  since,
  now,
  onOpen,
  onWatch,
}: {
  ticket: TicketSummary;
  live: boolean;
  since: number | undefined;
  now: number;
  onOpen: (t: TicketSummary) => void;
  onWatch: (t: TicketSummary) => void;
}) {
  const elapsed = live && since ? fmtElapsed(now - since) : null;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(ticket)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(ticket); } }}
      title={ticket.filename}
      className={`rounded-md border px-2 py-1.5 text-left cursor-pointer transition-colors ${
        live
          ? 'border-blue-500/40 bg-blue-500/10 hover:bg-blue-500/20 animate-[queue-lane-in_320ms_ease-out]'
          : 'border-base-300 bg-base-100 hover:bg-base-300/40'
      }`}
    >
      <div className="flex items-start gap-1.5">
        {live && (
          <span className="relative flex w-2 h-2 mt-1 shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-blue-500 opacity-60 animate-ping [animation-duration:2s]" />
            <span className="relative inline-flex w-2 h-2 rounded-full bg-blue-500" />
          </span>
        )}
        <span className="text-xs font-medium leading-snug line-clamp-2">{ticket.title}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-1.5 text-[10px] text-base-content/40">
        <span className="font-mono">{ticket.filename.split('-')[0]}</span>
        {ticket.epic && <span className="truncate max-w-16" title={ticket.epic}>{ticket.epic}</span>}
        {ticket.attempt_count > 0 && (
          <span className="text-warning font-mono" title={`${ticket.attempt_count} failed attempt${ticket.attempt_count === 1 ? '' : 's'}`}>
            ×{ticket.attempt_count}
          </span>
        )}
        {elapsed && <span className="font-mono text-blue-500/90 tabular-nums">{elapsed}</span>}
        <span className="flex-1" />
        {live && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onWatch(ticket); }}
            title="Watch this worker's live output"
            className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded font-medium bg-blue-500/15 text-blue-500 hover:bg-blue-500/30 transition-colors"
          >
            <Terminal className="w-3 h-3" />
          </button>
        )}
        <EngineChip ticket={ticket} />
      </div>
    </div>
  );
}

// ─── Plan view: capture + shaping ────────────────────────────────────

function PlanView({
  projectId,
  inbox,
  epics,
}: {
  projectId: string;
  inbox: IdeaSummary[];
  epics: EpicSummary[];
}) {
  return (
    <div className="flex-1 overflow-y-auto pb-2">
      <Inbox projectId={projectId} inbox={inbox} />
      <Epics epics={epics} />
    </div>
  );
}

// ─── Inbox ─────────────────────────────────────────────────────────

/**
 * Modal to submit a feature request. Publishes to the project's configured
 * `ingest.publish` NATS subject via `queue.submitFeatureRequest`; the project's
 * own ingest consumer files it back into the queue and (if configured) opens a
 * GitHub issue. On success we refresh the inbox so the ingested idea appears.
 */
function FeatureRequestModal({
  projectId,
  onClose,
  onSubmitted,
}: {
  projectId: string;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const backend = useBackend();
  const pushOverlay = useUIStore((s) => s.pushOverlay);
  const popOverlay = useUIStore((s) => s.popOverlay);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    pushOverlay();
    return () => popOverlay();
  }, [pushOverlay, popOverlay]);

  const submit = useCallback(async () => {
    const t = title.trim();
    if (!t) return;
    setSubmitting(true);
    setError(null);
    try {
      await backend.queue.submitFeatureRequest(projectId, t, body.trim(), 'feature-request');
      onSubmitted();
      onClose();
    } catch (e) {
      logger.warn(TAG, 'submit_feature_request failed', e);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }, [title, body, projectId, backend, onClose, onSubmitted]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-base-100 rounded-xl shadow-2xl w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300">
          <div className="flex items-center gap-2 font-medium">
            <MessageSquarePlus className="w-4 h-4 text-primary" /> Submit feature request
          </div>
          <button type="button" className="btn btn-ghost btn-xs btn-square" onClick={onClose} title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-base-content/60">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short summary of the request"
              className="input input-bordered input-sm w-full mt-1"
              autoFocus
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void submit(); }
              }}
            />
          </div>
          <div>
            <label className="text-xs font-medium text-base-content/60">Details</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="What do you want, and why? (Cmd/Ctrl+Enter to submit)"
              className="textarea textarea-bordered textarea-sm w-full mt-1 min-h-24 text-sm"
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void submit(); }
              }}
            />
          </div>
          {error && (
            <div className="text-xs text-error break-words">
              Couldn’t submit: {error}
            </div>
          )}
          <div className="text-[11px] text-base-content/40">
            Published to this project’s queue — and opened as a GitHub issue if a tracker is configured.
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-base-300">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary btn-sm gap-1"
            onClick={submit}
            disabled={!title.trim() || submitting}
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MessageSquarePlus className="w-3.5 h-3.5" />}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}

function Inbox({ projectId, inbox }: { projectId: string; inbox: IdeaSummary[] }) {
  const backend = useBackend();
  const refreshInbox = useQueueStore((s) => s.refreshInbox);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [converting, setConverting] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);

  const submit = useCallback(async () => {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    try {
      await backend.queue.inboxCreate(projectId, text);
      setDraft('');
      await refreshInbox();
    } catch (e) {
      logger.warn(TAG, 'inbox_create failed', e);
    } finally {
      setSubmitting(false);
    }
  }, [draft, projectId, refreshInbox, backend]);

  const toggle = useCallback((filename: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename); else next.add(filename);
      return next;
    });
  }, []);

  const remove = useCallback(async (filename: string) => {
    await backend.queue.inboxDelete(projectId, filename);
    setSelected((prev) => { const n = new Set(prev); n.delete(filename); return n; });
    await refreshInbox();
  }, [projectId, refreshInbox, backend]);

  const planSelected = useCallback(async () => {
    if (selected.size === 0) return;
    setConverting(true);
    try {
      // Pass the selected filenames as a single comma-joined arg; the
      // planner prompt tells the agent how to read inbox/<name>.md.
      const arg = `--inbox ${[...selected].join(',')}`;
      await backend.queue.invokeScript(projectId, 'plan', arg);
      // Planner doesn't auto-link; user can convert files manually via
      // inboxLinkTicket if they want auto-archive on done.
      await refreshInbox();
    } catch (e) {
      logger.warn(TAG, 'planner invoke failed', e);
    } finally {
      setConverting(false);
    }
  }, [selected, projectId, refreshInbox, backend]);

  return (
    <div className="px-3 py-2 border-b border-base-300 bg-base-100 space-y-2">
      {showFeatureModal && (
        <FeatureRequestModal
          projectId={projectId}
          onClose={() => setShowFeatureModal(false)}
          onSubmitted={() => { void refreshInbox(); }}
        />
      )}
      <div className="flex items-center gap-2 text-xs font-medium text-base-content/60">
        <InboxIcon className="w-3.5 h-3.5" /> Ideas
        <span className="text-base-content/40">{inbox.length}</span>
        <div className="flex-1" />
        <button
          type="button"
          className="btn btn-ghost btn-xs gap-1"
          onClick={() => setShowFeatureModal(true)}
          title="Submit a feature request (published to the queue + GitHub)"
        >
          <MessageSquarePlus className="w-3 h-3" /> Request
        </button>
        {selected.size > 0 && (
          <button
            type="button"
            className="btn btn-primary btn-xs gap-1"
            onClick={planSelected}
            disabled={converting}
          >
            {converting ? <Loader2 className="w-3 h-3 animate-spin" /> : <ListTodo className="w-3 h-3" />}
            Plan from {selected.size} selected
          </button>
        )}
      </div>
      <div className="flex items-start gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Drop a thought… (Cmd/Ctrl+Enter to save)"
          className="textarea textarea-bordered textarea-sm flex-1 min-h-12 text-sm"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); void submit(); }
          }}
        />
        <div className="flex flex-col gap-1">
          <button
            type="button"
            onClick={submit}
            disabled={!draft.trim() || submitting}
            className="btn btn-primary btn-sm btn-square"
            title="Save idea"
          >
            <Plus className="w-4 h-4" />
          </button>
          <InboxMicButton onTranscribed={(text) => setDraft((d) => (d ? d + ' ' : '') + text)} />
        </div>
      </div>
      {inbox.length > 0 && (
        <ul className="space-y-1 max-h-32 overflow-y-auto">
          {inbox.map((it) => (
            <li
              key={it.filename}
              className={`flex items-center gap-2 px-2 py-1 rounded text-xs ${
                it.converted ? 'opacity-50 bg-base-200' : 'bg-base-200 hover:bg-base-300'
              }`}
            >
              <input
                type="checkbox"
                className="checkbox checkbox-xs"
                checked={selected.has(it.filename)}
                onChange={() => toggle(it.filename)}
                disabled={it.converted}
              />
              <span className="flex-1 truncate">
                {it.preview}
                {it.converted && it.linked_ticket && (
                  <span className="ml-2 text-base-content/40">→ {it.linked_ticket}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => void remove(it.filename)}
                className="btn btn-ghost btn-xs btn-square opacity-50 hover:opacity-100"
                title="Delete idea"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// LA-2 mic for the inbox. Simpler than TerminalMicButton — no terminal
// paste, no fallback paths; only the cloud-live flow.
function InboxMicButton({ onTranscribed }: { onTranscribed: (text: string) => void }) {
  const backend = useBackend();
  const [recording, setRecording] = useState(false);
  const sessionRef = useRef<VeLiveSession | null>(null);

  const start = useCallback(async () => {
    try {
      const cfg = await backend.transcription.getWhisperSettings();
      if (cfg.provider !== 'voice-engine' || !cfg.voiceEngineToken) {
        logger.warn(TAG, 'mic requires voice-engine provider + token');
        return;
      }
      const s = await veLive({
        baseUrl: cfg.voiceEngineUrl,
        token: cfg.voiceEngineToken,
        profile: 'ateliercode',
        strategy: 'local_agreement',
      });
      sessionRef.current = s;
      setRecording(true);
    } catch (e) {
      logger.warn(TAG, 'mic start failed', e);
    }
  }, [backend]);

  const stop = useCallback(async () => {
    const s = sessionRef.current;
    sessionRef.current = null;
    setRecording(false);
    if (!s) return;
    try {
      const text = await s.stop();
      if (text.trim()) onTranscribed(text.trim());
    } catch (e) {
      logger.warn(TAG, 'mic stop failed', e);
    }
  }, [onTranscribed]);

  return (
    <button
      type="button"
      onClick={() => (recording ? void stop() : void start())}
      className={`btn btn-sm btn-square ${recording ? 'btn-error animate-pulse' : 'btn-ghost'}`}
      title={recording ? 'Stop and transcribe' : 'Record idea'}
    >
      {recording ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
    </button>
  );
}

// ─── Epics ─────────────────────────────────────────────────────────

const EPIC_STATUS_TONE: Record<string, string> = {
  draft: 'badge-ghost',
  active: 'badge-info',
  done: 'badge-success',
};

function Epics({ epics }: { epics: EpicSummary[] }) {
  if (epics.length === 0) return null;
  return (
    <div className="px-3 pt-3">
      <SectionLabel icon={Layers} label="Epics" count={epics.length} />
      <div className="mt-1">
        {epics.map((e) => (
          <div
            key={e.filename}
            className="flex items-center gap-2 px-1.5 py-[5px] rounded-md hover:bg-base-200 text-xs"
            title={`${e.id}${e.idea ? ` · from ${e.idea}` : ''}`}
          >
            <span className="font-mono text-[10px] text-base-content/35 shrink-0">{e.id.replace(/^EPIC-/, '')}</span>
            <span className="flex-1 truncate font-medium">{e.title}</span>
            <span className="text-[10px] text-base-content/40 shrink-0">
              {e.ticket_count} ticket{e.ticket_count === 1 ? '' : 's'}
            </span>
            <span className={`badge badge-xs ${EPIC_STATUS_TONE[e.status] ?? 'badge-ghost'}`}>{e.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Engine chip ───────────────────────────────────────────────────

// Small clickable chip on each ticket row showing its execution engine
// (local Qwen vs Claude). Clicking flips it via the store's setEngine, which
// persists to the ticket frontmatter and refreshes the folder. Defaults to
// "local" display when unset (local is the queue's default engine).
function EngineChip({ ticket }: { ticket: TicketSummary }) {
  const setEngine = useQueueStore((s) => s.setEngine);
  const engine = ticket.engine ?? 'local';
  const isClaude = engine === 'claude';
  const Icon = isClaude ? Bot : Cpu;
  const flip = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      void setEngine(ticket.folder, ticket.filename, isClaude ? 'local' : 'claude');
    },
    [setEngine, ticket.folder, ticket.filename, isClaude],
  );
  return (
    <button
      type="button"
      onClick={flip}
      title={`Engine: ${engine} — click to switch to ${isClaude ? 'local' : 'claude'}`}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium shrink-0 transition-colors ${
        isClaude
          ? 'bg-primary/15 text-primary hover:bg-primary/30'
          : 'bg-base-300/60 text-base-content/50 hover:bg-base-300'
      }`}
    >
      <Icon className="w-2.5 h-2.5" />
      {engine}
    </button>
  );
}

// ─── Ticket drawer ─────────────────────────────────────────────────

function TicketDrawer({
  ticket,
  body,
  onClose,
}: {
  ticket: TicketSummary;
  body: string;
  onClose: () => void;
}) {
  // Esc closes the modal (standard modal UX; backdrop click also closes).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  // Strip a leading YAML frontmatter block so the rendered view shows just the
  // ticket content; the metadata (folder/attempt) is already in the header.
  const md = body.replace(/^---\n[\s\S]*?\n---\n?/, '');
  return createPortal(
    // Centered, roomy modal (not a side drawer, not full-screen) with margin
    // around it, scrollable body, and RENDERED markdown instead of raw text.
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-6 sm:p-10">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[85vh] bg-base-100 border border-base-300 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-base-300 shrink-0">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{ticket.title}</div>
            <div className="text-xs text-base-content/50 truncate">
              {ticket.folder}/{ticket.filename} · attempt {ticket.attempt_count}
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-6 py-5">
          <article className="prose prose-invert prose-stone max-w-none
            prose-headings:text-base-content prose-headings:font-semibold
            prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
            prose-p:text-base-content/90 prose-li:text-base-content/90
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-code:text-base-content prose-pre:bg-base-200 prose-pre:border prose-pre:border-base-300">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{md}</ReactMarkdown>
          </article>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Worker log drawer (live "pop-in" stream) ──────────────────────

// A live view of an in-progress ticket's worker output. Distinct from the
// markdown TicketDrawer: this polls `queue.read_log(projectId, ticket)` every
// ~1s and renders the (ANSI-stripped) tail in an auto-scrolling monospace
// <pre>. A portal drawer, like TicketDrawer — Esc / backdrop / close button
// dismiss it, and the polling interval stops on close/unmount.
function WorkerLogDrawer({
  projectId,
  ticket,
  onClose,
}: {
  projectId: string;
  ticket: TicketSummary;
  onClose: () => void;
}) {
  const backend = useBackend();
  const [log, setLog] = useState<string>('');
  const [loaded, setLoaded] = useState(false);
  const preRef = useRef<HTMLPreElement | null>(null);
  // Only auto-scroll when the user is already pinned to the bottom, so manual
  // scroll-up to read earlier output isn't yanked back down on each poll.
  const pinnedRef = useRef(true);

  // Esc closes (matches TicketDrawer; backdrop click also closes).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Poll the worker log every ~1s while open. Stops on close/unmount.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const raw = await backend.queue.readLog(projectId, ticket.filename);
        if (cancelled) return;
        setLog(stripAnsi(raw));
      } catch (e) {
        logger.debug(TAG, 'read_log failed', e);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    void poll();
    const id = window.setInterval(() => void poll(), 1000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [projectId, ticket.filename, backend]);

  // Auto-scroll to the bottom on new content when pinned.
  useEffect(() => {
    const el = preRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [log]);

  const onScroll = useCallback(() => {
    const el = preRef.current;
    if (!el) return;
    // Within 24px of the bottom counts as "pinned".
    pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  }, []);

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-6 sm:p-10">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-4xl max-h-[85vh] bg-base-100 border border-base-300 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-base-300 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Terminal className="w-4 h-4 text-blue-500" />
              <span className="truncate">{ticket.title}</span>
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-500 text-[10px] font-semibold">
                <Activity className="w-3 h-3 animate-pulse" />
                live
              </span>
            </div>
            <div className="text-xs text-base-content/50 truncate">
              {ticket.folder}/{ticket.filename}
            </div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <pre
          ref={preRef}
          onScroll={onScroll}
          className="flex-1 overflow-auto px-4 py-3 m-0 text-xs font-mono leading-relaxed whitespace-pre-wrap break-words bg-base-200 text-base-content/90"
        >
          {log.length > 0
            ? log
            : (
              <span className="text-base-content/40">
                {loaded ? 'waiting for output…' : 'loading…'}
              </span>
            )}
        </pre>
      </div>
    </div>,
    document.body,
  );
}

// ─── Action bar ─────────────────────────────────────────────────────

// ─── Queue run state (script invocation) ───────────────────────────

interface QueueRunState {
  /** Verb currently running ('plan' | 'work' | …) or null when idle. */
  running: string | null;
  /** Accumulated output of the last/current run (null = never ran). */
  lastOutput: string | null;
  start: (verb: string) => Promise<void>;
  stop: () => Promise<void>;
}

/** Owns the queue-script run lifecycle (start/stop/output/restore). Extracted
 *  from the old footer ActionBar so the header Run button and the bottom
 *  status strip can share one source of truth. */
function useQueueRun(projectId: string): QueueRunState {
  const backend = useBackend();
  const [running, setRunning] = useState<string | null>(null);
  const [lastOutput, setLastOutput] = useState<string | null>(null);
  // True while THIS component instance owns a live run (it has the
  // onRunOutput/onRunExit subscriptions). When true, those subscriptions own
  // the running→null transition and the list_runs poll must not touch it. When
  // false, `running` (if set) was *restored* from the core, so the poll is free
  // to clear it once the core no longer reports the run.
  const locallyOwned = useRef(false);

  // Run-state restore (fixes the "switch projects + back makes a running Loop
  // look idle" bug). `running` is local component state, lost on unmount. On
  // mount / project change — and on a short poll — ask the core which runs are
  // in flight via queue.list_runs and reflect this project's verb.
  useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      try {
        const runs = await backend.queue.listRuns();
        if (cancelled || locallyOwned.current) return; // local subscription wins
        const mine = runs.find((r) => r.projectId === projectId);
        setRunning(mine?.verb ?? null);
      } catch (e) {
        logger.debug(TAG, 'list_runs failed', e);
      }
    };
    void sync();
    const id = window.setInterval(() => void sync(), 4000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [projectId, backend]);

  const start = useCallback(async (verb: string) => {
    locallyOwned.current = true; // this instance now owns the running→null edge
    setRunning(verb);
    setLastOutput('');
    // Stream output live (ticket 059) via the backend's run-event subscriptions —
    // Tauri `queue-output`/`queue-run-exit` events under the desktop shell, the
    // matching `queue.*` IPC event frames under the CEF host (ticket 065).
    const unlistenOut = backend.queue.onRunOutput((_stream, line) => {
      setLastOutput((prev) => `${prev ?? ''}${line}\n`);
    });
    const unlistenExit = backend.queue.onRunExit((code) => {
      setLastOutput((prev) => `${prev ?? ''}\n[run finished — exit ${code ?? '?'}]`);
      locallyOwned.current = false;
      setRunning(null);
      unlistenOut();
      unlistenExit();
    });
    try {
      await backend.queue.startRun(projectId, verb);
    } catch (e) {
      setLastOutput(`Error: ${e instanceof Error ? e.message : String(e)}`);
      locallyOwned.current = false;
      setRunning(null);
      unlistenOut();
      unlistenExit();
    }
  }, [projectId, backend]);

  const stop = useCallback(async () => {
    try {
      await backend.queue.stopRun();
      // If this run was restored (not locally owned), no onRunExit will fire to
      // clear the spinner — drop it optimistically; the poll confirms.
      if (!locallyOwned.current) setRunning(null);
    } catch (e) {
      logger.debug(TAG, 'stop_queue_run failed', e);
    }
  }, [backend]);

  return { running, lastOutput, start, stop };
}

// Bottom status strip — present ONLY while a script runs or has output to
// show, so the panel stays chrome-free at rest. The running row shows the verb
// + a live spinner; output stays behind a <details> fold either way.
function RunStatusStrip({ run }: { run: QueueRunState }) {
  if (run.running === null && !run.lastOutput) return null;
  return (
    <div className="border-t border-base-300 bg-base-200 px-3 py-1.5 space-y-1">
      {run.running !== null && (
        <div className="flex items-center gap-2 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
          <span className="font-mono font-medium">{run.running}</span>
          <span className="text-base-content/50">running…</span>
          <div className="flex-1" />
          <button
            type="button"
            className="btn btn-ghost btn-xs text-error gap-1"
            onClick={() => void run.stop()}
            title="Stop the running queue script (and its workers)"
          >
            <Square className="w-3 h-3" />
            stop
          </button>
        </div>
      )}
      {run.lastOutput && (
        <details className="text-xs">
          <summary className="cursor-pointer text-[11px] text-base-content/50">
            {run.running !== null ? 'live output' : 'last run output'}
          </summary>
          <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap bg-base-100 p-2 rounded border border-base-300">{run.lastOutput}</pre>
        </details>
      )}
    </div>
  );
}
